import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link as LinkIcon, CheckCircle2, AlertCircle, ExternalLink, Loader2, Users, Trash2, ShieldAlert, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PW_KEY = "ditch_admin_pw";

const ROLE_LABEL = {
  founder: "Founder",
  admin: "Admin",
  recruiter: "Recruiter",
  tdc_manager: "TDC Manager",
  va: "VA",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

/**
 * Admin tab — Integrations status board.
 * Shows live status of Stripe, PayPal, Xero and provides
 * one-click "Connect Xero" OAuth handshake. Also lists all admin
 * users who have signed in (multi-admin Google auth).
 */
export const IntegrationsTab = ({ user = null } = {}) => {
  const [xero, setXero] = useState(null);
  const [paypal, setPayPal] = useState(null);
  const [talentHub, setTalentHub] = useState(null);
  const [gmail, setGmail] = useState(null);
  const [gmailFleet, setGmailFleet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);

  const adminEmailHint = (user?.email || "").toLowerCase().trim();

  const authHeadersWithEmail = () => {
    const h = { "X-Admin-Password": localStorage.getItem(PW_KEY) || "" };
    if (adminEmailHint) h["X-Admin-Email"] = adminEmailHint;
    return h;
  };

  const refresh = async () => {
    const pw = localStorage.getItem(PW_KEY) || "";
    try {
      const [x, p, th, g, fleet] = await Promise.all([
        axios.get(`${API}/xero/status`).then((r) => r.data).catch(() => ({ connected: false })),
        axios.get(`${API}/paypal/config`).then((r) => r.data).catch(() => ({ enabled: false })),
        axios.get(`${API}/admin/activity?action=external_candidate_ingested&limit=1`, {
          headers: { "X-Admin-Password": pw },
        }).then((r) => ({
          seen: (r.data?.activity || []).length > 0,
          last_ingested_at: r.data?.activity?.[0]?.occurred_at || null,
        })).catch(() => ({ seen: false, last_ingested_at: null })),
        axios.get(`${API}/admin/gmail/status`, { headers: authHeadersWithEmail() })
          .then((r) => r.data).catch(() => ({ connected: false })),
        axios.get(`${API}/admin/gmail/health`, { headers: { "X-Admin-Password": pw } })
          .then((r) => r.data).catch(() => null),
      ]);
      setXero(x); setPayPal(p); setTalentHub(th); setGmail(g); setGmailFleet(fleet);
    } catch (e) {
      console.warn("Integrations status fetch failed:", e?.message || e);
    }
  };

  const connectGmail = async () => {
    // Auto-use the signed-in admin's email; fall back to their input only if we
    // don't know who they are (legacy password path).
    let adminEmail = adminEmailHint;
    if (!adminEmail) {
      adminEmail = window.prompt(
        "Which admin email are you connecting Gmail for?\n(Use your own real Gmail address.)",
        gmail?.admin_email || "",
      );
      if (!adminEmail) return;
    }
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/oauth/gmail/authorize?admin_email=${encodeURIComponent(adminEmail)}`,
        { headers: authHeadersWithEmail() },
      );
      const w = window.open(r.data.authorize_url, "gmail-oauth", "width=520,height=680");
      // Poll every 3s until Gmail status flips to connected
      const timer = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/admin/gmail/status`, { headers: authHeadersWithEmail() });
          if (s.data?.connected) {
            clearInterval(timer);
            setGmail(s.data);
            try { w && w.close(); } catch (_) {}
            toast.success(`Gmail connected — ${s.data.gmail_email}`);
            refresh();
          }
        } catch (_) {}
      }, 3000);
      setTimeout(() => clearInterval(timer), 5 * 60 * 1000);
    } catch (e) {
      toast.error(`Couldn't start Gmail OAuth: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const disconnectGmail = async () => {
    if (!window.confirm("Disconnect Gmail? Candidate inbox sync will stop.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/gmail/disconnect`, {}, { headers: authHeadersWithEmail() });
      toast.success("Gmail disconnected.");
      refresh();
    } catch (e) {
      toast.error(`Disconnect failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const syncGmailNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/gmail/sync-now`, {}, { headers: authHeadersWithEmail() });
      toast.success(`Scanned ${r.data.scanned || 0} messages, matched ${r.data.matched || 0} to candidates.`);
      refresh();
    } catch (e) {
      toast.error(`Sync failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const loadAdmins = async () => {
    const pw = localStorage.getItem(PW_KEY);
    if (!pw) return;
    setAdminsLoading(true);
    try {
      const r = await axios.get(`${API}/auth/users`, { headers: { "X-Admin-Password": pw } });
      setAdmins(r.data?.users || []);
    } catch (e) {
      // Silent — legacy password won't have user list access
      console.warn("Admins fetch failed:", e?.message || e);
    } finally {
      setAdminsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    loadAdmins();
    // refresh if OAuth callback redirected back here
    if (window.location.search.includes("xero=connected")) {
      toast.success("Xero connected — sales will now sync automatically.");
      window.history.replaceState({}, "", "/admin");
    }
  }, []);

  const connectXero = () => {
    window.location.href = `${API}/xero/connect?redirect_to=/admin`;
  };

  const disconnectXero = async () => {
    if (!window.confirm("Disconnect Xero? Future sales will stop auto-syncing.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/xero/disconnect`);
      toast.success("Xero disconnected.");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const testHubWebhook = async () => {
    const pw = localStorage.getItem(PW_KEY);
    if (!pw) { toast.error("Sign in first."); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/external/webhook/test`, {},
        { headers: { "X-Admin-Password": pw } },
      );
      if (r.data?.delivered) {
        toast.success(`Webhook delivered — Hub responded ${r.data.http_status}.`);
      } else {
        toast(`Webhook posted but Hub returned ${r.data?.http_status || "no response"}.`, { icon: "⚠️" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Webhook test failed.");
    } finally { setBusy(false); }
  };

  const integrations = [
    {
      name: "Stripe",
      desc: "Card + Afterpay checkout for packages and Members subscriptions.",
      status: "live",
      note: "Live mode · AUD",
    },
    {
      name: "PayPal",
      desc: "PayPal Smart Buttons (with Pay in 4) alongside Stripe.",
      status: paypal?.enabled ? "live" : "needs_config",
      note: paypal?.enabled ? `Live mode · ${paypal.currency}` : "Add PAYPAL_CLIENT_ID in .env",
    },
    {
      name: "Resend",
      desc: "Transactional + marketing email — 24 templates + campaigns engine.",
      status: "live",
      note: "Domains verified for both brands · from-addresses in live mode",
    },
    {
      name: "Gmail sync",
      desc: "Per-admin OAuth. Auto-syncs your inbox — emails to/from candidates land in their Timeline + Notes. Compose from candidate detail.",
      status: gmail?.connected ? "live" : (gmail?.google_client_configured === false ? "needs_env" : "ready"),
      note: gmail?.connected
        ? `${gmail.gmail_email} · last sync ${gmail.last_sync_at ? new Date(gmail.last_sync_at).toLocaleString("en-AU",{ dateStyle:"short", timeStyle:"short" }) : "pending"}${gmail.last_error ? " · ⚠ " + gmail.last_error : ""}`
        : (adminEmailHint
            ? `Sign in as ${adminEmailHint} and click Connect Gmail — Google will ask for read + send permission.`
            : "Click Connect Gmail — Google will ask for read + send permission on your inbox."),
      action: gmail?.connected
        ? { label: "Disconnect", onClick: disconnectGmail, danger: true }
        : { label: "Connect Gmail", onClick: connectGmail, primary: true },
      secondaryAction: gmail?.connected
        ? { label: "Sync now", onClick: syncGmailNow }
        : null,
    },
    {
      name: "Xero",
      desc: "Auto-creates Contact + Invoice + Payment for every Stripe & PayPal sale.",
      status: xero?.connected ? "live" : "needs_oauth",
      note: xero?.connected
        ? `Connected: ${xero.tenant_name} · since ${new Date(xero.connected_at).toLocaleDateString("en-AU")}`
        : "Click Connect Xero to authorise",
      action: xero?.connected
        ? { label: "Disconnect", onClick: disconnectXero, danger: true }
        : { label: "Connect Xero", onClick: connectXero, primary: true },
    },
    {
      name: "Talent Hub",
      desc: "External candidate ingestion + outbound stage webhooks with theditchcareers.com.au.",
      status: talentHub?.seen ? "live" : "ready",
      note: talentHub?.seen
        ? `Last import: ${new Date(talentHub.last_ingested_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}`
        : "Endpoint live at POST /api/external/talent — waiting for first submission.",
      action: { label: "Send test webhook", onClick: testHubWebhook, primary: true },
    },
  ];

  return (
    <div data-testid="integrations-tab">
      <div className="mb-8">
        <h2 className="font-display text-2xl font-extrabold mb-2">Integrations</h2>
        <p className="text-sm text-[var(--ocean)]">
          Status of every service wired into The Ditch Collective. Reconnect / disconnect from here.
        </p>
      </div>

      <div className="grid gap-4">
        {integrations.map((i) => (
          <div
            key={i.name}
            data-testid={`integration-${i.name.toLowerCase()}`}
            className="bg-white border border-[var(--line)] rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--mist-soft)] flex items-center justify-center">
              {i.status === "live" ? (
                <CheckCircle2 size={20} className="text-[var(--ink)]" />
              ) : (
                <AlertCircle size={20} className="text-[var(--ocean)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="font-display font-bold text-lg">{i.name}</h3>
                <span
                  className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    i.status === "live"
                      ? "bg-[var(--lime)] text-[var(--ink)]"
                      : "bg-[var(--mist-soft)] text-[var(--ocean)]"
                  }`}
                >
                  {i.status === "live" ? "Connected" : i.status === "needs_oauth" ? "Action required" : i.status === "ready" ? "Ready" : "Setup"}
                </span>
              </div>
              <p className="text-sm text-[var(--ocean)] mb-1.5">{i.desc}</p>
              <p className="text-xs italic text-[var(--ink)]/60">{i.note}</p>
            </div>
            {i.action && (
              <button
                type="button"
                onClick={i.action.onClick}
                disabled={busy}
                data-testid={`btn-${i.name.toLowerCase()}-${i.action.primary ? "connect" : "disconnect"}`}
                className={`flex-shrink-0 inline-flex items-center gap-2 font-display font-bold rounded-full px-5 py-2.5 text-sm transition-colors disabled:opacity-50 ${
                  i.action.primary
                    ? "bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--ink-soft)]"
                    : "border border-[var(--line)] text-[var(--ink)] hover:border-[var(--ink)]"
                }`}
              >
                {busy ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : i.action.primary ? (
                  <LinkIcon size={14} />
                ) : null}
                {i.action.label}
                {i.action.primary && <ExternalLink size={14} />}
              </button>
            )}
            {i.secondaryAction && (
              <button
                type="button"
                onClick={i.secondaryAction.onClick}
                disabled={busy}
                data-testid={`btn-${i.name.toLowerCase()}-secondary`}
                className="flex-shrink-0 inline-flex items-center gap-2 font-display font-bold rounded-full px-5 py-2.5 text-sm border border-[var(--line)] text-[var(--ink)] hover:border-[var(--ink)] disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {i.secondaryAction.label}
              </button>
            )}
          </div>
        ))}
      </div>

      {xero?.connected && (
        <div className="mt-8 bg-[var(--mist-soft)] rounded-2xl p-5 text-sm">
          <p className="font-display font-bold mb-2 text-[var(--ink)]">Heads-up — one small Xero setup</p>
          <p className="text-[var(--ocean)] mb-2">
            For tracked-category reporting (Packages / Subscriptions / Guides), open Xero →{" "}
            <b>Accounting → Advanced → Tracking categories</b>, add a category called{" "}
            <b>"Revenue Type"</b> with options <b>Packages</b>, <b>Subscriptions</b>, <b>Guides</b>.
          </p>
          <p className="text-[var(--ocean)]">
            Optional but recommended — invoices flow with or without it.
          </p>
        </div>
      )}

      {/* ── Gmail Fleet panel — every admin's connected inbox ─────────── */}
      {gmailFleet && (gmailFleet.connections?.length ?? 0) > 0 && (
        <div className="mt-10" data-testid="gmail-fleet-panel">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={20} className="text-[var(--ink)]" />
            <h2 className="font-display text-2xl font-extrabold">Team Gmail connections</h2>
            <span className="text-xs font-bold uppercase tracking-widest bg-[var(--mist-soft)] text-[var(--ocean)] px-2 py-0.5 rounded-full">
              {gmailFleet.connections.length}
            </span>
          </div>
          <p className="text-sm text-[var(--ocean)] mb-5">
            Each admin connects <b>their own</b> Gmail here. That's how their sent emails and inbound replies flow into a candidate's Timeline + Notes automatically. Everyone signs in with Google above, then clicks <b>Connect Gmail</b> — the connection is stored under their email.
          </p>
          <div className="bg-white rounded-2xl border border-[var(--line)] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--mist-soft)] text-[10px] uppercase tracking-widest text-[var(--ocean)]">
                <tr>
                  <th className="text-left px-4 py-3">Admin</th>
                  <th className="text-left px-4 py-3">Gmail inbox</th>
                  <th className="text-left px-4 py-3">Last sync</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {gmailFleet.connections.map((c, idx) => (
                  <tr key={c.admin_email || idx} className="border-t border-[var(--line)]">
                    <td className="px-4 py-3 font-semibold text-[var(--ink)]">{c.admin_email}</td>
                    <td className="px-4 py-3 text-[var(--ink)]">{c.gmail_email || "—"}</td>
                    <td className="px-4 py-3 text-[var(--ocean)]">
                      {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString("en-AU",{ dateStyle:"short", timeStyle:"short" }) : "pending"}
                      {typeof c.minutes_since_sync === "number" ? ` · ${c.minutes_since_sync}m ago` : ""}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {c.last_error ? (
                        <span className="inline-flex items-center gap-1 text-red-700"><AlertCircle size={12}/>{c.last_error}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12}/>healthy</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[var(--ocean)] mt-3">
            Poll interval: every {Math.round((gmailFleet.poll_interval_seconds || 180) / 60)} min.
            Total synced messages: <b>{gmailFleet.counters?.total_synced_messages ?? 0}</b>
            {" · "}last 24h: <b>{gmailFleet.counters?.synced_24h ?? 0}</b>.
          </p>
        </div>
      )}

      {/* ── Admins panel ─────────────────────────────────────────── */}
      <div className="mt-10" data-testid="admins-panel">
        <div className="flex items-center gap-2 mb-4">
          <Users size={20} className="text-[var(--ink)]" />
          <h2 className="font-display text-2xl font-extrabold">Admins</h2>
          <span className="text-xs font-bold uppercase tracking-widest bg-[var(--mist-soft)] text-[var(--ocean)] px-2 py-0.5 rounded-full">
            {admins.length}
          </span>
        </div>
        <p className="text-sm text-[var(--ocean)] mb-5">
          Everyone who has signed in via Google. Access is locked to{" "}
          <b className="text-[var(--ink)]">@theditchcollective.co.nz</b> and{" "}
          <b className="text-[var(--ink)]">@theditchcareers.com.au</b>.
        </p>

        {adminsLoading ? (
          <p className="text-sm text-[var(--ocean)]">Loading admins…</p>
        ) : admins.length === 0 ? (
          <div
            className="bg-white border border-[var(--line)] rounded-2xl p-6 text-sm text-[var(--ocean)] text-center"
            data-testid="admins-empty"
          >
            No admins have signed in yet. (If you're using the legacy password,
            sign in with Google once to appear here.)
          </div>
        ) : (
          <div className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--mist-soft)] text-[var(--ocean)] text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-5 py-3">User</th>
                  <th className="text-left px-5 py-3">Role</th>
                  <th className="text-left px-5 py-3">Last seen</th>
                  <th className="text-left px-5 py-3">Joined</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr
                    key={a.user_id}
                    data-testid={`admin-row-${a.user_id}`}
                    className="border-t border-[var(--line)]"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {a.picture ? (
                          <img
                            src={a.picture}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <span className="w-8 h-8 rounded-full bg-[var(--lime)] text-[var(--ink)] font-bold flex items-center justify-center text-xs">
                            {(a.first_name?.[0] || a.email?.[0] || "?").toUpperCase()}
                          </span>
                        )}
                        <div>
                          <p className="font-semibold text-[var(--ink)]">
                            {[a.first_name, a.last_name].filter(Boolean).join(" ") || a.email}
                          </p>
                          <p className="text-xs text-[var(--ocean)]">{a.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          a.role === "founder"
                            ? "bg-[var(--lime)] text-[var(--ink)]"
                            : "bg-[var(--mist-soft)] text-[var(--ocean)]"
                        }`}
                        data-testid={`admin-role-${a.user_id}`}
                      >
                        {ROLE_LABEL[a.role] || a.role || "Admin"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[var(--ocean)]">{fmtDate(a.last_login_at)}</td>
                    <td className="px-5 py-3 text-[var(--ocean)]">{fmtDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Danger zone ─────────────────────────────────────────── */}
      <DangerZone />
    </div>
  );
};


// ── Danger zone (purge test data) ───────────────────────────────
const DangerZone = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [sweepPreview, setSweepPreview] = useState(null);
  const [sweepBusy, setSweepBusy] = useState(false);

  const previewSweep = async () => {
    setSweepBusy(true);
    try {
      const pw = localStorage.getItem(PW_KEY);
      const r = await axios.get(`${API}/admin/danger/staging-sweep/preview`, {
        headers: { "X-Admin-Password": pw },
      });
      setSweepPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview sweep.");
    } finally { setSweepBusy(false); }
  };

  const runSweepNow = async () => {
    if (!window.confirm(`Run the staging sweep now? Will purge ${sweepPreview?.total || 0} test rows immediately.`)) return;
    setSweepBusy(true);
    try {
      const pw = localStorage.getItem(PW_KEY);
      const r = await axios.post(`${API}/admin/danger/staging-sweep/run-now`, {}, {
        headers: { "X-Admin-Password": pw },
      });
      toast.success(`Sweep complete — ${r.data.total} rows purged.`);
      setSweepPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed.");
    } finally { setSweepBusy(false); }
  };

  const preview = async () => {
    setLoading(true);
    try {
      const pw = localStorage.getItem(PW_KEY);
      const r = await axios.post(
        `${API}/admin/danger/purge-test-data`,
        { dry_run: true, confirm: "" },
        { headers: { "X-Admin-Password": pw } },
      );
      setDryRun(r.data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load counts.");
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    if (confirmText !== "DELETE") {
      toast.error("Type DELETE exactly to confirm.");
      return;
    }
    if (!window.confirm(
      `This will permanently delete ${dryRun?.total || 0} rows across ${Object.keys(dryRun?.would_delete || {}).length} collections. There is no undo. Continue?`
    )) return;
    setLoading(true);
    try {
      const pw = localStorage.getItem(PW_KEY);
      const r = await axios.post(
        `${API}/admin/danger/purge-test-data`,
        { dry_run: false, confirm: "DELETE" },
        { headers: { "X-Admin-Password": pw } },
      );
      toast.success(`Purged ${r.data.total_deleted} rows.`);
      setOpen(false);
      setDryRun(null);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed.");
    } finally {
      setLoading(false);
    }
  };

  // ── Legacy leads → relocation_leads migration ─────────────────────
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [recoveryPreview, setRecoveryPreview] = useState(null);
  const previewBackfill = async () => {
    setBackfillBusy(true);
    try {
      const pw = localStorage.getItem(PW_KEY);
      const r = await axios.post(
        `${API}/admin/leads/backfill-from-legacy?dry_run=true`,
        {},
        { headers: { "X-Admin-Password": pw } },
      );
      setBackfillPreview(r.data);
      setBackfillResult(null);
      toast.success(`Would migrate ${r.data.would_insert} legacy lead${r.data.would_insert === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally { setBackfillBusy(false); }
  };
  const runBackfill = async () => {
    if (!window.confirm(
      "Migrate every doc in the legacy `leads` collection into `relocation_leads`, " +
      "hydrating melbourne_area → target_city on blank-field records?\n\n" +
      "Idempotent — already-migrated leads are skipped.",
    )) return;
    setBackfillBusy(true);
    setBackfillPreview(null);
    try {
      const pw = localStorage.getItem(PW_KEY);
      const r = await axios.post(
        `${API}/admin/leads/backfill-from-legacy`,
        {},
        { headers: { "X-Admin-Password": pw } },
      );
      setBackfillResult(r.data);
      const { inserted, blank_field_repaired, already_present } = r.data;
      toast.success(
        `Repaired ${inserted} lead${inserted === 1 ? "" : "s"}` +
        (blank_field_repaired ? ` (${blank_field_repaired} blank-field fix${blank_field_repaired === 1 ? "" : "es"})` : "") +
        (already_present ? ` — ${already_present} already migrated` : ""),
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed.");
    } finally { setBackfillBusy(false); }
  };

  // ── Abandoned Move Recovery ────────────────────────────────────────
  const callRecovery = async (dryRun) => {
    setRecoveryBusy(true);
    try {
      const pw = localStorage.getItem(PW_KEY);
      const r = await axios.post(
        `${API}/admin/leads/recover-abandoned${dryRun ? "?dry_run=true" : ""}`,
        {},
        { headers: { "X-Admin-Password": pw } },
      );
      if (dryRun) {
        setRecoveryPreview(r.data);
        setRecoveryResult(null);
        toast.success(`Would nudge ${r.data.would_send} abandoned popup subscriber${r.data.would_send === 1 ? "" : "s"}.`);
      } else {
        setRecoveryResult(r.data);
        setRecoveryPreview(null);
        toast.success(`Nudged ${r.data.sent} abandoned popup subscriber${r.data.sent === 1 ? "" : "s"}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery run failed.");
    } finally { setRecoveryBusy(false); }
  };
  const previewRecovery = () => callRecovery(true);
  const runRecovery = () => {
    if (!window.confirm(
      "Send the 'Ready to tell us about your move?' nudge to every popup subscriber " +
      "who signed up 48h+ ago and never finished /move?\n\n" +
      "One send per person, ever.",
    )) return;
    callRecovery(false);
  };

  return (
    <div className="mt-10 rounded-2xl border-2 border-[#B91C1C]/30 bg-[#FDECEC]/30 p-6" data-testid="danger-zone">
      <div className="flex items-start gap-3 mb-4">
        <ShieldAlert size={22} className="text-[#B91C1C] flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="font-display text-xl font-extrabold text-[#B91C1C]">Danger zone</h2>
          <p className="text-sm text-[var(--ocean)] mt-1">
            Wipe test data. Preserves admin users, Xero connection, discounts, and product catalog.
          </p>
        </div>
      </div>

      {/* Staging sweep — auto + on-demand */}
      <div className="bg-white rounded-xl p-5 border border-[#B91C1C]/20 mb-4" data-testid="staging-sweep">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="font-display font-bold text-[var(--ink)] text-sm">Nightly staging sweep</p>
            <p className="text-xs text-[var(--ocean)] mt-0.5 max-w-xl leading-relaxed">
              Auto-purges obvious test emails (<span className="font-mono">@example.com</span>, <span className="font-mono">@test.*</span>, <span className="font-mono">iter_*</span>, <span className="font-mono">e2e_*</span>) every night at 3am AEDT so testing doesn't build up cruft. Preview or trigger manually below.
            </p>
          </div>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <button
            onClick={previewSweep}
            disabled={sweepBusy}
            data-testid="sweep-preview-btn"
            className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full bg-white text-[var(--ocean)] border border-[#D6DCE3] hover:border-[var(--ink)]"
          >
            {sweepBusy ? "…" : "Preview"}
          </button>
          {sweepPreview && (
            <>
              <span className="text-sm text-[var(--ink)]">
                Would purge <b className="text-[#B91C1C]">{sweepPreview.total}</b> rows
              </span>
              <button
                onClick={runSweepNow}
                disabled={sweepBusy || sweepPreview.total === 0}
                data-testid="sweep-run-btn"
                className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full bg-[#B91C1C] text-white hover:bg-[#991717] disabled:opacity-40"
              >
                Run sweep now
              </button>
            </>
          )}
        </div>
        {sweepPreview && sweepPreview.total > 0 && (
          <div className="mt-3 text-xs font-mono grid grid-cols-2 gap-x-4 gap-y-0.5">
            {Object.entries(sweepPreview.report || {})
              .filter(([, n]) => n > 0)
              .sort(([, a], [, b]) => b - a)
              .map(([col, n]) => (
                <div key={col} className="flex justify-between border-b border-[#ECF0F4] py-0.5">
                  <span className="text-[var(--ocean)]">{col}</span>
                  <span className="text-[#B91C1C] font-bold">{n}</span>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Repair legacy leads — migrates `leads` → `relocation_leads` */}
      <div className="bg-white rounded-xl p-5 border border-[#B91C1C]/20 mb-4" data-testid="repair-legacy-leads">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <p className="font-display font-bold text-[var(--ink)] text-sm">Repair legacy leads</p>
            <p className="text-xs text-[var(--ocean)] mt-0.5 max-w-xl leading-relaxed">
              One-click migration of historic docs from the old <span className="font-mono">leads</span> collection into the unified <span className="font-mono">relocation_leads</span> list.
              Fills in <span className="font-mono">target_city</span> from <span className="font-mono">melbourne_area</span> on blank-field records.
              Idempotent — already-migrated leads are skipped.
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <button
              onClick={previewBackfill}
              disabled={backfillBusy}
              data-testid="repair-legacy-leads-preview-btn"
              className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full bg-white text-[var(--ocean)] border border-[#D6DCE3] hover:border-[var(--ink)] disabled:opacity-40"
            >
              {backfillBusy ? "…" : "Preview"}
            </button>
            <button
              onClick={runBackfill}
              disabled={backfillBusy}
              data-testid="repair-legacy-leads-btn"
              className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full bg-[#B91C1C] text-white hover:bg-[#991717] disabled:opacity-40 inline-flex items-center gap-2"
            >
              {backfillBusy ? "Repairing…" : "Repair legacy leads"}
            </button>
          </div>
        </div>
        {backfillPreview && !backfillResult && (
          <div className="mt-2 text-xs font-mono grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid="repair-legacy-leads-preview-result">
            <div className="flex justify-between border-b border-[#ECF0F4] py-0.5">
              <span className="text-[var(--ocean)]">scanned</span>
              <span className="font-bold">{backfillPreview.scanned}</span>
            </div>
            <div className="flex justify-between border-b border-[#ECF0F4] py-0.5">
              <span className="text-[var(--ocean)]">would migrate</span>
              <span className="font-bold text-[#B91C1C]">{backfillPreview.would_insert}</span>
            </div>
            <div className="flex justify-between border-b border-[#ECF0F4] py-0.5">
              <span className="text-[var(--ocean)]">blank-field repairs</span>
              <span className="font-bold text-[#0D2F4D]">{backfillPreview.blank_field_repaired}</span>
            </div>
            <div className="flex justify-between border-b border-[#ECF0F4] py-0.5">
              <span className="text-[var(--ocean)]">already migrated</span>
              <span className="font-bold">{backfillPreview.already_present}</span>
            </div>
          </div>
        )}
        {backfillResult && (
          <div className="mt-2 text-xs font-mono grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid="repair-legacy-leads-result">
            <div className="flex justify-between border-b border-[#ECF0F4] py-0.5">
              <span className="text-[var(--ocean)]">scanned</span>
              <span className="font-bold">{backfillResult.scanned}</span>
            </div>
            <div className="flex justify-between border-b border-[#ECF0F4] py-0.5">
              <span className="text-[var(--ocean)]">inserted</span>
              <span className="font-bold text-[#C6D21F]">{backfillResult.inserted}</span>
            </div>
            <div className="flex justify-between border-b border-[#ECF0F4] py-0.5">
              <span className="text-[var(--ocean)]">blank-field repaired</span>
              <span className="font-bold text-[#0D2F4D]">{backfillResult.blank_field_repaired}</span>
            </div>
            <div className="flex justify-between border-b border-[#ECF0F4] py-0.5">
              <span className="text-[var(--ocean)]">already migrated (skipped)</span>
              <span className="font-bold">{backfillResult.already_present}</span>
            </div>
          </div>
        )}
      </div>

      {/* Abandoned Move Recovery — nudges popup subscribers who never finished /move */}
      <div className="bg-white rounded-xl p-5 border border-[#B91C1C]/20 mb-4" data-testid="abandoned-recovery">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <p className="font-display font-bold text-[var(--ink)] text-sm">Abandoned move recovery</p>
            <p className="text-xs text-[var(--ocean)] mt-0.5 max-w-xl leading-relaxed">
              Sends the <span className="font-mono">abandoned_move_nudge</span> email to every popup
              subscriber who signed up more than 48h ago and never finished /move. One nudge per
              person, ever. Skips fixture domains + unsubscribes automatically.
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <button
              onClick={previewRecovery}
              disabled={recoveryBusy}
              data-testid="abandoned-recovery-preview-btn"
              className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full bg-white text-[var(--ocean)] border border-[#D6DCE3] hover:border-[var(--ink)] disabled:opacity-40"
            >
              {recoveryBusy ? "…" : "Preview"}
            </button>
            <button
              onClick={runRecovery}
              disabled={recoveryBusy}
              data-testid="abandoned-recovery-run-btn"
              className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full bg-[#B91C1C] text-white hover:bg-[#991717] disabled:opacity-40 inline-flex items-center gap-2"
            >
              {recoveryBusy ? "Sending…" : "Send nudges"}
            </button>
          </div>
        </div>
        {(recoveryPreview || recoveryResult) && (
          <div className="mt-2 text-xs font-mono grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid="abandoned-recovery-result">
            {["scanned", "eligible", recoveryResult ? "sent" : "would_send", "already_completed", "out_of_window", "fixture_skipped"].map((k) => (
              <div key={k} className="flex justify-between border-b border-[#ECF0F4] py-0.5">
                <span className="text-[var(--ocean)]">{k.replace(/_/g, " ")}</span>
                <span className="font-bold">
                  {(recoveryResult || recoveryPreview)?.[k] ?? 0}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nuclear purge — wipes everything, not just test-pattern emails */}
      {!open ? (
        <button
          onClick={preview}
          disabled={loading}
          data-testid="danger-purge-preview"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#B91C1C] border-2 border-[#B91C1C] hover:bg-[#B91C1C] hover:text-white font-display font-bold text-sm transition-colors disabled:opacity-50"
        >
          <Trash2 size={14} /> {loading ? "Loading…" : "Purge test data"}
        </button>
      ) : (
        <div className="bg-white rounded-xl p-5 border border-[#B91C1C]/30">
          <p className="text-sm text-[#0D2F4D] font-semibold mb-3">
            Will delete <b className="text-[#B91C1C]">{dryRun?.total || 0} rows</b> across{" "}
            {Object.keys(dryRun?.would_delete || {}).length} collections:
          </p>
          <div className="max-h-40 overflow-y-auto text-xs font-mono grid grid-cols-2 gap-x-4 gap-y-1 mb-4">
            {Object.entries(dryRun?.would_delete || {})
              .sort(([, a], [, b]) => b - a)
              .map(([col, n]) => (
                <div key={col} className="flex justify-between border-b border-[#ECF0F4] py-0.5">
                  <span className="text-[var(--ocean)]">{col}</span>
                  <span className={n > 0 ? "text-[#B91C1C] font-bold" : "text-[var(--ocean)]/50"}>{n}</span>
                </div>
              ))}
          </div>
          <p className="text-xs text-[var(--ocean)] mb-3">
            Preserved: <span className="font-mono">{dryRun?.preserved?.join(", ")}</span>
          </p>
          <label className="block text-xs font-bold uppercase tracking-widest text-[#B91C1C] mb-1">
            Type <span className="font-mono">DELETE</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            data-testid="danger-confirm-input"
            className="w-full max-w-xs rounded-lg border-2 border-[#B91C1C]/40 bg-white px-4 py-2 text-sm font-mono focus:outline-none focus:border-[#B91C1C]"
          />
          <div className="mt-4 flex gap-3">
            <button
              onClick={execute}
              disabled={loading || confirmText !== "DELETE"}
              data-testid="danger-confirm-execute"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#B91C1C] text-white font-display font-bold text-sm hover:bg-[#991717] disabled:opacity-30 transition-colors"
            >
              <Trash2 size={14} /> {loading ? "Deleting…" : "Delete forever"}
            </button>
            <button
              onClick={() => { setOpen(false); setDryRun(null); setConfirmText(""); }}
              className="text-sm text-[var(--ocean)] hover:text-[var(--ink)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
